import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTicketById } from "@/hooks/useTickets";
import { useAuth } from "@/hooks/useAuth";
import { useComments } from "@/hooks/useComments";
import { apiClient } from "@/lib/api";
import { cn, getErrorMessage, getAttachmentViewUrl, getAttachmentFileName, getAttachmentFileSize } from "@/lib/utils";
import { Stage, AuthUser, TicketAssignee, Comment as TicketComment, TicketPriority } from "@/lib/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PriorityBadge } from "@/components/ui/priority-badge";
import { StageBadge } from "@/components/ui/stage-badge";
import { DaysSinceSubmissionBadge, getDaysSinceSubmission } from "@/components/ui/days-since-badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Building,
  Calendar,
  Clock,
  Edit,
  Save,
  Plus,
  X,
  Paperclip,
  MessageSquare,
  FileText,
  Loader2,
  Search,
  Check,
} from "lucide-react";
import { format, isValid } from "date-fns";
import { toast } from "sonner";

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const [comment, setComment] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [estimatedValue, setEstimatedValue] = useState("");
  const [estimatedType, setEstimatedType] = useState<"" | "hours" | "days" | "weeks">("");
  const [priority, setPriority] = useState("");
  const [stageId, setStageId] = useState("");
  const [stages, setStages] = useState<Stage[]>([]);
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [unassigningUserId, setUnassigningUserId] = useState<string | null>(null);
  const [showUserSelect, setShowUserSelect] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const { data: ticket, isLoading, refetch } = useTicketById(id!);
  const { comments, addComment: addCommentMutation } = useComments(id!);

  const isAssignee = ticket?.assignees?.some(
    (a: TicketAssignee) => (a.userId || (a as unknown as { user_id?: string }).user_id) === user?.id
  );
  const canUpdateSpecifics = isAdmin || isAssignee;

  // Fetch stages and users on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [stagesData, usersData] = await Promise.all([
          apiClient.getStages(),
          apiClient.getUsers(),
        ]);
        console.log("Stages fetched:", stagesData);
        console.log("Users fetched:", usersData);
        setStages(stagesData);
        // Handle both snake_case and camelCase
        setUsers(usersData.filter((u: AuthUser) => 
          (u.profile?.isActive !== false) && 
          ((u.profile as unknown as { is_active?: boolean })?.is_active !== false)
        ));
      } catch (error) {
        console.error("Failed to fetch data:", error);
        toast.error(getErrorMessage(error, "Failed to load stages or users"));
      }
    };
    fetchData();
  }, []);

  // Set initial values when ticket loads
  useEffect(() => {
    if (ticket) {
      // Handle both camelCase and snake_case from backend
      const ticketStageId = (ticket as unknown as { stage_id?: string }).stage_id || ticket.stageId;
      const ticketPriority = ticket.priority;
      const ticketDueDate = (ticket as unknown as { due_date?: string }).due_date || ticket.dueDate;
      const ticketEstimatedValue = (ticket as unknown as { estimated_value?: number }).estimated_value || ticket.estimatedValue;
      const ticketEstimatedType = (ticket as unknown as { estimated_type?: string }).estimated_type || ticket.estimatedType;

      console.log("Setting ticket values:", {
        priority: ticketPriority,
        stageId: ticketStageId,
        dueDate: ticketDueDate,
        estimatedValue: ticketEstimatedValue,
        estimatedType: ticketEstimatedType
      });

      setPriority(ticketPriority || "");
      setStageId(ticketStageId || "");
      setDueDate(ticketDueDate?.split("T")[0] || "");
      setEstimatedValue(ticketEstimatedValue?.toString() || "");
      setEstimatedType((ticketEstimatedType as "hours" | "days" | "weeks") || "hours");
    }
  }, [ticket]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Ticket Not Found</h2>
        <Button variant="outline" onClick={() => navigate("/admin/tickets")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tickets
        </Button>
      </div>
    );
  }

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    try {
      await addCommentMutation.mutateAsync({
        content: comment,
        isInternal,
      });
      setComment("");
      setIsInternal(false);
      await refetch();
    } catch (error: unknown) {
      console.error("Failed to add comment:", error);
      toast.error(getErrorMessage(error, "Failed to add comment"));
    }
  };

  const handleStageChange = async (newStageId: string) => {
    if (!id) return;
    try {
      await apiClient.updateTicketStage(id, newStageId);
      setStageId(newStageId);
      toast.success("Stage updated successfully");
      await refetch();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to update stage"));
    }
  };

  const handlePriorityChange = async (newPriority: string) => {
    if (!id) return;
    try {
      await apiClient.updateTicket(id, { priority: newPriority as TicketPriority });
      setPriority(newPriority);
      toast.success("Priority updated successfully");
      await refetch();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to update priority"));
    }
  };

  const handleUpdateTicket = async () => {
    if (!id) return;
    setIsUpdating(true);
    try {
      const updateData: Record<string, unknown> = {
        dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
        estimatedValue: estimatedValue ? parseInt(estimatedValue) : undefined,
        estimatedType: estimatedType || undefined,
      };

      // Only admins can update priority
      if (isAdmin) {
        updateData.priority = priority as TicketPriority;
      }

      await apiClient.updateTicket(id, updateData);
      toast.success("Ticket updated successfully");
      await refetch();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to update ticket"));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAssignUsers = async (userIds: string[]) => {
    if (!id || userIds.length === 0) return;
    setIsAssigning(true);
    try {
      // Merge with existing assignees
      const existingIds = (ticket?.assignees ?? [])
        .map((a: TicketAssignee) => a.userId || (a as unknown as { user_id?: string }).user_id)
        .filter((id): id is string => Boolean(id));
      const mergedIds = Array.from(new Set([...existingIds, ...userIds]));
      await apiClient.assignTicket(id, mergedIds);
      toast.success(`${userIds.length} user(s) assigned successfully`);
      await refetch();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to assign users"));
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassignUser = async (userId: string) => {
    if (!id) return;
    setUnassigningUserId(userId);
    try {
      await apiClient.unassignTicket(id, [userId]);
      toast.success("User unassigned successfully");
      await refetch();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to unassign user"));
    } finally {
      setUnassigningUserId(null);
    }
  };

  const handleAssignUserSelect = async () => {
    if (selectedUserIds.length === 0 || !id) return;
    await handleAssignUsers(selectedUserIds);
    setSelectedUserIds([]);
    setShowUserSelect(false);
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/tickets")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <h1 className="text-2xl font-bold">{ticket.ticketNumber}</h1>
              <DaysSinceSubmissionBadge days={getDaysSinceSubmission(ticket)} />
              <StageBadge stage={ticket.stage} />
              <PriorityBadge priority={ticket.priority} />
            </div>
            <p className="text-muted-foreground">{ticket.ticketType?.name}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{ticket.description}</p>
              {((ticket as any).expected_outcome || ticket.expectedOutcome) && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Expected Outcome</h4>
                  <p className="text-muted-foreground">
                    {(ticket as any).expected_outcome || ticket.expectedOutcome}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Attachments (from submit / requester; use download_url per TICKET_FLOW.md) */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Paperclip className="h-5 w-5" />
                  Attachments ({ticket.attachments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {ticket.attachments.map((attachment) => {
                    const viewUrl = getAttachmentViewUrl(attachment as Record<string, unknown>);
                    const canOpen = viewUrl && viewUrl !== "#";
                    const name = getAttachmentFileName(attachment as Record<string, unknown>);
                    const size = getAttachmentFileSize(attachment as Record<string, unknown>);
                    const isImage = (attachment as { file_type?: string; fileType?: string }).file_type?.startsWith("image/") ?? (attachment as { fileType?: string }).fileType?.startsWith("image/");
                    return (
                      <div key={attachment.id} className="flex flex-col gap-1">
                        {canOpen ? (
                          <a
                            href={viewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                          >
                            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="text-sm truncate min-w-0">{name}</span>
                            {size != null && (
                              <span className="text-xs text-muted-foreground ml-auto shrink-0">
                                {Math.round(size / 1024)} KB
                              </span>
                            )}
                          </a>
                        ) : (
                          <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
                            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="text-sm truncate min-w-0">{name}</span>
                            <span className="text-xs text-muted-foreground ml-auto">Unavailable</span>
                          </div>
                        )}
                        {isImage && canOpen && (
                          <a href={viewUrl} target="_blank" rel="noopener noreferrer" className="block rounded border overflow-hidden max-w-xs">
                            <img src={viewUrl} alt={name} className="max-h-40 w-full object-contain bg-muted/30" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Comments ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {comments && comments.length > 0 ? (
                <div className="space-y-4">
                  {comments.map((comment: TicketComment) => {
                    const commentDate = (comment as unknown as { created_at?: string }).created_at || comment.createdAt;
                    const date = commentDate ? new Date(commentDate) : null;
                    const formattedDate =
                      date && isValid(date)
                        ? format(date, "MMM d, yyyy h:mm a")
                        : "—";
                    return (
                      <div
                        key={comment.id}
                        className={`p-4 rounded-lg border content-container ${
                          (comment as unknown as { is_internal?: boolean }).is_internal || comment.isInternal
                            ? "bg-amber-50/50 border-amber-200"
                            : (comment as unknown as { is_from_requester?: boolean }).is_from_requester || comment.isFromRequester
                            ? "bg-primary/5 border-primary/20 ml-8"
                            : "bg-muted/50 mr-8"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarImage
                                src={
                                  comment.user?.profile?.avatarUrl || 
                                  (comment.user?.profile as unknown as { avatar_url?: string })?.avatar_url
                                }
                              />
                              <AvatarFallback>
                                {comment.user?.profile?.name?.charAt(0) || 
                                 ((comment as unknown as { is_from_requester?: boolean }).is_from_requester || comment.isFromRequester ? "R" : "A")}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium">
                              {comment.user?.profile?.name || 
                               ((comment as unknown as { is_from_requester?: boolean }).is_from_requester || comment.isFromRequester ? "Requester" : "System")}
                            </span>
                          </div>
                          {(comment as unknown as { is_internal?: boolean }).is_internal || comment.isInternal ? (
                            <Badge
                              variant="outline"
                              className="text-[10px] uppercase bg-amber-100 text-amber-700 border-amber-200"
                            >
                              Internal
                            </Badge>
                          ) : null}
                          <span className="text-xs text-muted-foreground ml-auto">
                            {formattedDate}
                          </span>
                        </div>
                        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                          {comment.content}
                        </p>
                        {/* Comment attachments (per TICKET_FLOW.md: comments[].attachments with download_url) */}
                        {((comment as unknown as { attachments?: Array<Record<string, unknown>> }).attachments?.length ?? 0) > 0 && (
                          <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
                            <span className="text-xs font-medium text-muted-foreground">Attachments</span>
                            <div className="flex flex-wrap gap-2">
                              {((comment as unknown as { attachments: Array<Record<string, unknown>> }).attachments).map((att: Record<string, unknown>) => (
                                <a
                                  key={String(att.id)}
                                  href={getAttachmentViewUrl(att)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1.5 px-2 py-1.5 rounded border text-xs hover:bg-muted/50 transition-colors"
                                >
                                  <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                                  <span className="truncate max-w-[160px]">{getAttachmentFileName(att)}</span>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No comments yet
                </p>
              )}

              <Separator />

              {/* Add Comment */}
              <div className="space-y-3">
                <Textarea
                  placeholder="Add a comment..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="min-h-[80px]"
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="internal"
                      checked={isInternal}
                      onCheckedChange={setIsInternal}
                    />
                    <Label htmlFor="internal" className="text-sm">
                      Internal comment
                    </Label>
                  </div>
                  <Button onClick={handleAddComment} disabled={!comment.trim()}>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Add Comment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Management */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Status Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Stage</Label>
                <Select
                  value={stageId}
                  onValueChange={handleStageChange}
                  disabled={!isAdmin}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select stage..." />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {stage.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Priority</Label>
                <Select
                  value={priority}
                  onValueChange={handlePriorityChange}
                  disabled={!isAdmin}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select priority..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={dueDate || ((ticket.dueDate ?? (ticket as unknown as { due_date?: string }).due_date)?.split("T")[0] ?? "")}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="mt-1"
                  disabled={!canUpdateSpecifics}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Estimation Type</Label>
                  <Select
                    value={estimatedType}
                    onValueChange={(value: "hours" | "days" | "weeks") => setEstimatedType(value)}
                    disabled={!canUpdateSpecifics}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                      <SelectItem value="weeks">Weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Value</Label>
                  <Input
                    type="number"
                    placeholder={
                      ticket.estimatedValue?.toString() || "0"
                    }
                    value={estimatedValue}
                    onChange={(e) => setEstimatedValue(e.target.value)}
                    className="mt-1"
                    disabled={!canUpdateSpecifics}
                  />
                </div>
              </div>

              <Button
                onClick={handleUpdateTicket}
                className="w-full"
                disabled={!canUpdateSpecifics || isUpdating}
              >
                <Save className="h-4 w-4 mr-2" />
                {isUpdating ? "Updating..." : "Update Ticket"}
              </Button>
            </CardContent>
          </Card>

          {/* Assignees */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>Assigned To</span>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowUserSelect(!showUserSelect)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {showUserSelect && isAdmin && (
                <div className="space-y-4 border rounded-md p-3 bg-muted/30">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-muted-foreground">
                      Search and select users to add:
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between h-auto min-h-10 bg-background hover:bg-background/80"
                        >
                          <div className="flex flex-wrap gap-1 p-1">
                            {selectedUserIds.length > 0 ? (
                              selectedUserIds.map((id) => {
                                const user = users.find((u: AuthUser) => u.id === id);
                                return (
                                  <Badge
                                    key={id}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {user?.profile?.name || user?.email || "Unknown"}
                                    <X
                                      className="h-3 w-3 cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleUserSelection(id);
                                      }}
                                    />
                                  </Badge>
                                );
                              })
                            ) : (
                              <span className="text-muted-foreground text-xs ml-1">Select users...</span>
                            )}
                          </div>
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command className="w-full">
                          <CommandInput placeholder="Search users..." />
                          <CommandList className="max-h-60">
                            <CommandEmpty>No users found.</CommandEmpty>
                            <CommandGroup>
                              {users
                                .filter((u: AuthUser) => {
                                  const isAssigned = ticket.assignees?.some((a: TicketAssignee) => {
                                    const assigneeUserId = a.userId || (a as unknown as { user_id?: string }).user_id;
                                    return assigneeUserId === u.id;
                                  });
                                  return !isAssigned;
                                })
                                .map((user: AuthUser) => (
                                  <CommandItem
                                    key={user.id}
                                    onSelect={() => toggleUserSelection(user.id)}
                                    className="flex items-center gap-2 cursor-pointer"
                                  >
                                    <div
                                      className={cn(
                                        "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                        selectedUserIds.includes(user.id)
                                          ? "bg-primary text-primary-foreground"
                                          : "opacity-50 [&_svg]:invisible"
                                      )}
                                    >
                                      <Check className="h-3 w-3" />
                                    </div>
                                    <span className="flex-1">{user.profile?.name || user.email}</span>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      onClick={handleAssignUserSelect}
                      disabled={selectedUserIds.length === 0 || isAssigning}
                      className="flex-1 h-8"
                    >
                      {isAssigning ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          Assigning...
                        </>
                      ) : (
                        <>Assign {selectedUserIds.length > 0 ? `(${selectedUserIds.length})` : ""}</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowUserSelect(false);
                        setSelectedUserIds([]);
                      }}
                      className="flex-1 h-8"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {ticket.assignees && ticket.assignees.length > 0 ? (
                <div className="space-y-2">
                  {ticket.assignees.map((assignee: TicketAssignee) => (
                    <div
                      key={assignee.id}
                      className="flex items-center justify-between p-2 rounded-lg border"
                    >
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={assignee.user.profile?.avatarUrl} />
                          <AvatarFallback>
                            {assignee.user.profile?.name?.charAt(0) ?? "U"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">
                          {assignee.user.profile?.name}
                        </span>
                      </div>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnassignUser(assignee.userId || (assignee as any).user_id)}
                          disabled={unassigningUserId === (assignee.userId || (assignee as any).user_id)}
                        >
                          {unassigningUserId === (assignee.userId || (assignee as any).user_id) ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <X className="h-3 w-3" />
                          )}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No assignees yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Ticket Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Ticket Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Requester</p>
                  <p className="font-medium">{(ticket as any).requester_name || ticket.requesterName}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <a
                    href={`mailto:${(ticket as any).requester_email || ticket.requesterEmail}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {(ticket as any).requester_email || ticket.requesterEmail}
                  </a>
                </div>
              </div>

              {((ticket as any).requester_phone || ticket.requesterPhone) && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Phone</p>
                    <p className="font-medium">{(ticket as any).requester_phone || ticket.requesterPhone}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Building className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Department</p>
                  <p className="font-medium">{ticket.department?.name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">
                    {((ticket as any).created_at || ticket.createdAt)
                      ? format(new Date((ticket as any).created_at || ticket.createdAt), "MMM d, yyyy h:mm a")
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {ticket.dueDate && (
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="font-medium">
                      {format(new Date(ticket.dueDate), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>
              )}

              {ticket.relatedSystem && (
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Related System</p>
                    <p className="font-medium capitalize">
                      {ticket.relatedSystem}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
